/**
 * UniversalChatProvider - Single gate check + capabilities fetch for Universal Chat
 *
 * Wraps the app at root level. Performs the gate check once and caches the
 * result in universalChatStore, so individual projections (AgenticChatBubble,
 * Studio ChatInterface) don't each need their own fetch.
 *
 * See: docs/internal/gonzo/UNIVERSAL_CHAT_ARCHITECTURE.md
 */

import { useEffect, type ReactNode } from 'react'
import { useUniversalChatStore } from '../stores/universalChatStore'

interface UniversalChatProviderProps {
  children: ReactNode
}

export default function UniversalChatProvider({ children }: UniversalChatProviderProps) {
  const gateOpen = useUniversalChatStore((s) => s.gateOpen)
  const setGateOpen = useUniversalChatStore((s) => s.setGateOpen)
  const setCapabilities = useUniversalChatStore((s) => s.setCapabilities)

  // Gate check — runs once on mount
  useEffect(() => {
    if (gateOpen !== null) return // already checked

    let cancelled = false

    async function checkGate() {
      try {
        const res = await fetch('/api/chat-agent/gate-status')
        if (!res.ok) {
          if (!cancelled) setGateOpen(false)
          return
        }
        const data = await res.json()
        if (!cancelled) setGateOpen(!!data.enabled)
      } catch {
        if (!cancelled) setGateOpen(false)
      }
    }

    checkGate()
    return () => { cancelled = true }
  }, [gateOpen, setGateOpen])

  // Capabilities fetch — runs once when gate opens
  useEffect(() => {
    if (gateOpen !== true) return

    let cancelled = false

    async function fetchCapabilities() {
      try {
        const res = await fetch('/api/chat-agent/capabilities')
        if (!res.ok) return
        const caps = await res.json()
        if (!cancelled) setCapabilities(caps)
      } catch {
        // Non-critical — capabilities will show as null (read-only mode)
      }
    }

    fetchCapabilities()
    return () => { cancelled = true }
  }, [gateOpen, setCapabilities])

  return <>{children}</>
}
